import { ChangeDetectorRef, Component, forwardRef, Input, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';

import {
  ClassicEditor,
  InlineEditor,
  type EditorConfig
} from 'ckeditor5';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { editConfig, viewConfig } from './editor-configs';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs';
import { HelperService } from 'app/shared/services/helper.service';
import { ImageUploadAdapter } from './image-upload.adapter';
import { BlobStorageService } from 'app/shared/services/blob-storage.service';
import { TranslateFieldValueComponent } from 'app/shared/components/translate-field-value/translate-field-value.component';
import { InputHelperTextComponent } from 'app/shared/components/input-helper-text/input-helper-text.component';

@Component({
  selector: 'app-rich-text-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ReactiveFormsModule, CKEditorModule, TranslateFieldValueComponent, InputHelperTextComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    }
  ],
  templateUrl: './rich-text-editor.component.html',
  styleUrl: './rich-text-editor.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class RichTextEditorComponent implements ControlValueAccessor {
  @Input() readonly!: boolean;
  @Input() label: string = '';
  @Input() type: string = 'text';
  @Input() placeholder: string = 'placeholders.type_text_here';
  @Input() isRequired: boolean = false;
  @Input() infoText: string = '';
  @Input() editableInline: boolean = true;
  @Input() value!: string;
  @Input() translatable: boolean = true;
  @Input() helpText!: string;
  inlineEditMode!: boolean;

  translated: string = '';
  formControl: FormControl = new FormControl<string>('');
  errorText: string = '';

  private unsubscribe$ = new Subject<void>()

  constructor(
    private changeDetector: ChangeDetectorRef,
    private helperService: HelperService,
    private blobStorageService: BlobStorageService
  ) { }

  public isLayoutReady = false;
  public ViewEditor = InlineEditor;
  public EditEditor = ClassicEditor;
  public editConfig: EditorConfig = {};
  public viewConfig: EditorConfig = {};

  public ngAfterViewInit(): void {
    this.editConfig = editConfig;
    this.viewConfig = viewConfig;
    this.isLayoutReady = true;
    this.changeDetector.detectChanges();
  }

  public onReady(editor: any) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
      return new ImageUploadAdapter(loader, this.blobStorageService);
    };
  }

  public onReadyView(editor: any) {
    editor.isReadOnly = true;
  }

  onTouch: () => void = () => { };
  onChange: (value: any) => void = () => { };

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.formControl.disable() : this.formControl.enable();
  }

  writeValue(value: string): void {
    this.formControl.setValue(value, { emitEvent: false });
  }

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((value) => {
        this.onChange(value);
      });
  }

  validate(control: FormControl) {
    setTimeout(() => {
      this.errorText = this.helperService.handleFieldsErrors(control);
      this.changeDetector.markForCheck();
    });
    return null;
  }

  translate(event: string) {
    this.translated = event;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
